import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type { HandbookListItem } from "@/types/handbook";
import type { PaginationMeta } from "@/types/common";
import type { UsePaginationReturn } from "@/hooks/use-pagination";
import { getHandbookColumns } from "./handbook-columns";
import type { HandbookAction } from "./handbook-actions";
import { ArticlePagination } from "../articles/article-pagination";
import {
  usePublishHandbook,
  useUnpublishHandbook,
  useDeleteHandbook,
} from "@/hooks/use-handbook";

interface HandbookTableProps {
  data: HandbookListItem[];
  meta: PaginationMeta;
  pagination: UsePaginationReturn;
}

export function HandbookTable({
  data,
  meta,
  pagination,
}: HandbookTableProps) {
  const navigate = useNavigate();
  const publishMutation = usePublishHandbook();
  const unpublishMutation = useUnpublishHandbook();
  const deleteMutation = useDeleteHandbook();

  const handleAction = useCallback(
    (item: HandbookListItem, action: HandbookAction) => {
      if (action === "edit") {
        navigate(`/handbook/${item._id}/edit`);
        return;
      }
      if (action === "publish") {
        publishMutation.mutate(item._id);
        return;
      }
      if (action === "unpublish") {
        unpublishMutation.mutate(item._id);
        return;
      }
      if (action === "delete") {
        if (
          !confirm(
            `'${item.title}' 페이지를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`,
          )
        )
          return;
        deleteMutation.mutate(item._id);
      }
    },
    [navigate, publishMutation, unpublishMutation, deleteMutation],
  );

  const parentTitleById = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of data) {
      map.set(item._id, item.title);
    }
    return map;
  }, [data]);

  const columns = useMemo(
    () => getHandbookColumns(handleAction, { parentTitleById }),
    [handleAction, parentTitleById],
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (data.length === 0) {
    return (
      <div className="rounded-lg border p-8 text-center">
        <p className="text-muted-foreground">자료집 페이지가 없습니다.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="overflow-auto rounded-md border">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-card">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                className="cursor-pointer transition-colors duration-75 hover:bg-muted/50"
                onClick={() => navigate(`/handbook/${row.original._id}/edit`)}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    // actions 컬럼은 자체 클릭 (publish/unpublish/delete) — row navigate 와 충돌하므로
                    // 셀 단에서 stopPropagation. 다른 컬럼은 모두 row 클릭 = 편집 화면 이동.
                    onClick={
                      cell.column.id === "actions"
                        ? (e) => e.stopPropagation()
                        : undefined
                    }
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <ArticlePagination meta={meta} pagination={pagination} />
    </div>
  );
}
